import React, { useState, useEffect } from 'react';
import PostInstallSuccess from './PostInstallSuccess';
import IOSInstallInstructions from './IOSInstallInstructions';

const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showPostInstall, setShowPostInstall] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false); // New: iPhone instructions modal
  const [isWaitingForPrompt, setIsWaitingForPrompt] = useState(false); // New: Loading state while waiting for prompt

  useEffect(() => {
    console.log('🐾 PWA Banner: Component mounted');
    
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
                    || window.navigator.standalone === true;
    setIsStandalone(standalone);
    console.log('🐾 PWA Banner: Standalone mode?', standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    console.log('🐾 PWA Banner: iOS device?', iOS);

    // Check if mobile
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobile);
    console.log('🐾 PWA Banner: Mobile device?', mobile);

    // Don't show if already installed or not mobile
    if (standalone) {
      console.log('🐾 PWA Banner: Already installed, not showing banner');
      return;
    }
    
    if (!mobile) {
      console.log('🐾 PWA Banner: Not a mobile device, not showing banner');
      return;
    }

    // Show banner for both Android AND iPhone (removed iPhone exclusion)
    console.log('🐾 PWA Banner: Mobile device detected, preparing banner');
    localStorage.removeItem('pwa-banner-dismissed');
    localStorage.removeItem('pwa-banner-dismissed-time');

    // For Android - capture the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('🐾 PWA Banner: beforeinstallprompt event fired!');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner after 0.2 seconds
      setTimeout(() => {
        console.log('🐾 PWA Banner: Showing banner (Android)');
        setShowBanner(true);
      }, 200);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For Android: Show banner after 0.2 seconds even if beforeinstallprompt doesn't fire
    const fallbackTimer = setTimeout(() => {
      if (!deferredPrompt && !showBanner) {
        console.log('🐾 PWA Banner: No beforeinstallprompt event, showing fallback banner');
        setShowBanner(true);
      }
    }, 200);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(fallbackTimer);
    };
  }, []);

  const handleInstall = async () => {
    console.log('🐾 PWA Banner: Install button clicked');
    console.log('🐾 PWA Banner: Has deferred prompt?', !!deferredPrompt);
    
    if (!deferredPrompt) {
      console.log('🐾 PWA Banner: No install prompt available yet, waiting...');
      setIsWaitingForPrompt(true);
      
      // Wait up to 3 seconds for beforeinstallprompt to fire
      const waitForPrompt = new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (deferredPrompt) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
        
        // Timeout after 3 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve(false);
        }, 3000);
      });
      
      const promptAvailable = await waitForPrompt;
      setIsWaitingForPrompt(false);
      
      if (!promptAvailable && !deferredPrompt) {
        console.log('🐾 PWA Banner: Timeout - prompt not available, showing manual instructions');
        alert('To install:\n1. Open Chrome menu (⋮)\n2. Tap "Install app" or "Add to Home screen"');
        return;
      }
    }

    // Android install with prompt
    try {
      console.log('🐾 PWA Banner: Showing install prompt');
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log('🐾 PWA Banner: User choice:', outcome);
      if (outcome === 'accepted') {
        console.log('🐾 PWA Banner: User accepted PWA install');
        
        // Hide banner
        setShowBanner(false);
        setDeferredPrompt(null);
        
        // Show post-install success screen
        setTimeout(() => {
          setShowPostInstall(true);
        }, 500);
      } else {
        console.log('🐾 PWA Banner: User dismissed PWA install');
      }
    } catch (error) {
      console.error('🐾 PWA Banner: Error showing install prompt:', error);
      setIsWaitingForPrompt(false);
      alert('To install:\n1. Open Chrome menu (⋮)\n2. Tap "Install app" or "Add to Home screen"');
    }
  };

  const handleContinue = () => {
    console.log('🐾 PWA Banner: Continue to website clicked');
    setShowBanner(false);
    // Note: Not setting dismissal flags - banner will show again on next visit
    // This allows users to reinstall if they delete the app
  };

  const handleClose = () => {
    console.log('🐾 PWA Banner: Close button clicked');
    setShowBanner(false);
    // Note: Not setting dismissal flags - banner will show again on next visit
  };

  if (isStandalone) {
    return null;
  }

  // Show post-install success screen if triggered
  if (showPostInstall) {
    return <PostInstallSuccess onClose={() => setShowPostInstall(false)} />;
  }

  // Show iOS install instructions if triggered
  if (showIOSInstructions) {
    return (
      <IOSInstallInstructions 
        onClose={() => setShowIOSInstructions(false)}
        onGoToMap={() => {
          setShowIOSInstructions(false);
          window.location.href = '/map';
        }}
      />
    );
  }

  if (!showBanner) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 flex items-start justify-center z-[99999] pointer-events-none"
      style={{ paddingTop: '20px' }}
    >
      <div 
        className="pwa-banner pointer-events-auto"
        style={{
          animation: 'unfurlBanner 0.8s ease-out forwards',
          transformOrigin: 'top center',
          maxHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div 
          className="bg-emerald-600 text-white rounded-2xl shadow-2xl relative"
          style={{
            maxWidth: '90vw',
            width: '400px',
            padding: '20px',
          }}
        >
          {/* Close button - orange circle with X */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="text-center pr-8">
            <div className="text-4xl mb-3">🐾</div>
            <h3 className="text-xl font-bold mb-2">Install Happy Paws</h3>
            <p className="text-sm mb-4 text-emerald-50">
              Choose your device type
            </p>

            {/* Two Buttons: Android or iPhone */}
            <div className="flex flex-col gap-3 mt-4" style={{ animation: 'slideUpButtons 0.4s ease-out 0.6s both' }}>
              <button
                onClick={handleInstall}
                disabled={isWaitingForPrompt}
                className="bg-white text-emerald-700 font-semibold py-3 px-4 rounded-lg hover:bg-emerald-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-wait"
              >
                {isWaitingForPrompt ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Preparing install...</span>
                  </>
                ) : (
                  <>
                    <span>🤖</span>
                    <span>I have an Android phone</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => {
                  setShowBanner(false);
                  setShowIOSInstructions(true);
                }}
                className="bg-white text-emerald-700 font-semibold py-3 px-4 rounded-lg hover:bg-emerald-50 transition-all flex items-center justify-center gap-2"
              >
                <span>🍎</span>
                <span>I have an iPhone</span>
              </button>
              
              <button
                onClick={handleContinue}
                className="bg-emerald-700 text-white font-medium py-2 px-4 rounded-lg hover:bg-emerald-800 transition-all flex items-center justify-center gap-2 text-sm"
              >
                <span>🐾</span>
                <span>Continue to Website</span>
                <span>🐾</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes unfurlBanner {
          0% {
            max-height: 0;
            opacity: 0;
            transform: scaleY(0);
          }
          60% {
            max-height: 500px;
            opacity: 0.8;
            transform: scaleY(1);
          }
          100% {
            max-height: 500px;
            opacity: 1;
            transform: scaleY(1);
          }
        }

        @keyframes slideUpButtons {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PWAInstallBanner;
